import { useCallback, useEffect, useRef } from "react";

/**
 * Guarda contra las dos carreras que tiene toda escritura de la pantalla
 * Marca, que son la misma pregunta: cuando la respuesta vuelve, ¿todavía
 * corresponde aplicarla?
 *
 *  1. El usuario cambió de marca mientras el `update` estaba en vuelo. La
 *     escritura salió bien (para la marca VIEJA, que es lo que el usuario
 *     pidió y queda persistido igual), pero el panel ya está mostrando otra
 *     marca: pintar "Guardado" o salir del modo edición ahí sería aplicarle a
 *     la marca nueva el resultado de una operación que no era suya.
 *
 *  2. El usuario se fue de la pantalla. Cualquier `setState` posterior es una
 *     fuga sobre un componente desmontado.
 *
 * Se devuelve una función y no un booleano porque el chequeo tiene que hacerse
 * DESPUÉS del `await`, con el id que se capturó ANTES de disparar la
 * escritura: `const id = marca.id; … if (!sigueVigente(id)) return;`.
 */
export function useEscrituraVigente(marcaId: string) {
  const marcaIdRef = useRef(marcaId);
  const montadoRef = useRef(true);

  useEffect(() => {
    marcaIdRef.current = marcaId;
  }, [marcaId]);

  // El `= true` del cuerpo (y no solo el `= false` de la limpieza) es lo que
  // lo hace correcto bajo StrictMode, que monta, desmonta y vuelve a montar:
  // sin él, el segundo montaje arrancaría con la ref ya en `false`.
  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  return useCallback(
    (idAlDisparar: string) => montadoRef.current && marcaIdRef.current === idAlDisparar,
    [],
  );
}
